import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Radio, Space, Typography } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import './Security.scss';

type EnrollmentMode = 'password' | 'mpin';

const { Paragraph, Title, Text } = Typography;

const SecurityPage: React.FC = () => {
  const [enrollmentMode, setEnrollmentMode] = useState<EnrollmentMode>('password');
  const [newMpin, setNewMpin] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentMpin, setCurrentMpin] = useState('');
  const [confirmMpin, setConfirmMpin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validationError = useMemo(() => {
    if (!newMpin && !confirmMpin) {
      return null;
    }

    if (!/^(\d{4}|\d{6})$/.test(newMpin)) {
      return 'New MPIN must be exactly 4 or 6 digits';
    }

    if (newMpin !== confirmMpin) {
      return 'MPIN confirmation does not match';
    }

    return null;
  }, [confirmMpin, newMpin]);

  const activeCurrentCredential =
    enrollmentMode === 'password' ? currentPassword : currentMpin;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validationError || !newMpin || !confirmMpin || !activeCurrentCredential.trim()) {
      return;
    }

    setSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await authService.setMpin(
        enrollmentMode === 'password'
          ? {
              newMpin,
              currentPassword,
            }
          : {
              newMpin,
              currentMpin,
            },
      );

      setSuccessMessage(
        enrollmentMode === 'password'
          ? 'MPIN enrolled successfully. Use it on your next login.'
          : 'MPIN updated successfully.',
      );
      setNewMpin('');
      setCurrentPassword('');
      setCurrentMpin('');
      setConfirmMpin('');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to update MPIN');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="security-page">
      <Space direction="vertical" size={24} className="security-stack">
        <div>
          <Title level={2}>Security</Title>
          <Paragraph>
            AminID now supports MPIN-first sign-in. Use this page to enroll your first MPIN with your current password or rotate an existing MPIN.
          </Paragraph>
        </div>

        <Card>
          <Space direction="vertical" size={12} className="security-card-copy">
            <Text strong>
              <SafetyCertificateOutlined /> MPIN rollout guidance
            </Text>
            <Paragraph>
              Password login is still available during rollout, but MPIN should be treated as the primary AminID credential moving forward.
            </Paragraph>
          </Space>
        </Card>

        <Card title="Enroll or rotate MPIN">
          <form onSubmit={handleSubmit}>
            <Space direction="vertical" size={18} className="security-form-stack">
              <div>
                <Text strong>Enrollment mode</Text>
                <Radio.Group
                  className="security-mode-group"
                  value={enrollmentMode}
                  onChange={(event) => {
                    setEnrollmentMode(event.target.value as EnrollmentMode);
                    setSuccessMessage(null);
                    setErrorMessage(null);
                  }}
                >
                  <Radio.Button value="password">First-time setup with password</Radio.Button>
                  <Radio.Button value="mpin">Rotate existing MPIN</Radio.Button>
                </Radio.Group>
              </div>

              {enrollmentMode === 'password' ? (
                <Form.Item label="Current password" required>
                  <Input.Password
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    placeholder="Enter your current password"
                    autoComplete="current-password"
                  />
                </Form.Item>
              ) : (
                <Form.Item label="Current MPIN" required>
                  <Input.Password
                    value={currentMpin}
                    onChange={(event) =>
                      setCurrentMpin(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    placeholder="Enter your current MPIN"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </Form.Item>
              )}

              <Form.Item label="New MPIN" required>
                <Input.Password
                  prefix={<LockOutlined />}
                  value={newMpin}
                  onChange={(event) =>
                    setNewMpin(event.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Enter a 4- or 6-digit MPIN"
                  inputMode="numeric"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item label="Confirm new MPIN" required>
                <Input.Password
                  value={confirmMpin}
                  onChange={(event) =>
                    setConfirmMpin(event.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Confirm your new MPIN"
                  inputMode="numeric"
                  autoComplete="new-password"
                />
              </Form.Item>

              {validationError ? <Alert type="error" showIcon message={validationError} /> : null}
              {errorMessage ? <Alert type="error" showIcon message={errorMessage} /> : null}
              {successMessage ? <Alert type="success" showIcon message={successMessage} /> : null}

              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={Boolean(validationError) || !newMpin || !confirmMpin || !activeCurrentCredential.trim()}
              >
                {enrollmentMode === 'password' ? 'Enroll MPIN' : 'Update MPIN'}
              </Button>
            </Space>
          </form>
        </Card>
      </Space>
    </div>
  );
};

export default SecurityPage;